// CKeyboardHintBox — 左上角半透明鍵盤操作提示框（DOM 覆蓋層）
// 顯示多行文字，常駐於畫面左上角。內容由外部傳入（setLines），本元件不關心訊息語意，
// 只負責「畫一個半透明黑底白字的提示框」。
//
// 為什麼用 DOM 而不是 Phaser.Text：
//   Phaser canvas 在 FIT 模式下 backing store 固定 1280×720，會被 CSS 放大到視窗大小、
//   retina 螢幕再 ×2，小字（14px）經兩階縮放會明顯模糊。改用瀏覽器原生 DOM 元素，
//   字型由系統渲染，自動 retina-aware，完全銳利。提示框只是純文字 UI、不參與遊戲座標
//   邏輯，做成 DOM 反而簡單。
//
// 典型用法（CGamePreviewController 內）：
//   const hint = new CKeyboardHintBox(scene);
//   hint.setLines(['【預覽模式 鍵盤操作】', 'PageDown：輪換莊家', 'A：顯示 action bar']);
//   // 之後狀態變動時呼叫 setLines 重畫；不再需要時 destroy()
//
// === 使用圖片 ===
// 無 — 純 HTML <div>，用 CSS 樣式呈現

import type Phaser from 'phaser';

// ---- 顯示位置（畫面左上角預設值，可由 config 覆寫） ----
const DEFAULT_X = 20; // 距視窗左緣（CSS px）
const DEFAULT_Y = 20; // 距視窗頂端（CSS px）

// ---- 視覺樣式（CSS） ----
const FONT_FAMILY = 'sans-serif';
const FONT_SIZE = '14px';
const LINE_HEIGHT = '1.5';
const PAD_X = '12px';
const PAD_Y = '10px';
const TEXT_COLOR = '#ffffff';
const BG_COLOR = 'rgba(0,0,0,0.55)';
const BORDER_RADIUS = '4px';
const Z_INDEX = '1000'; // 蓋過 canvas

export type TKeyboardHintBoxConfig = {
  /** 距視窗左緣 px，省略時用 DEFAULT_X。 */
  readonly x?: number;
  /** 距視窗頂端 px，省略時用 DEFAULT_Y。 */
  readonly y?: number;
};

/**
 * 左上角鍵盤操作提示框（DOM overlay 實作）。
 * 對外 API 與舊版 Phaser.Text 版本相同：setLines / setVisible / destroy。
 * Scene 參數仍保留，方便未來需要時把 element 掛在 scene container 下做生命週期管理。
 */
export class CKeyboardHintBox {
  private readonly element: HTMLDivElement;

  constructor(scene: Phaser.Scene, config: TKeyboardHintBoxConfig = {}) {
    const x = config.x ?? DEFAULT_X;
    const y = config.y ?? DEFAULT_Y;

    const el = document.createElement('div');
    Object.assign(el.style, {
      position: 'fixed',
      top: `${y}px`,
      left: `${x}px`,
      fontFamily: FONT_FAMILY,
      fontSize: FONT_SIZE,
      lineHeight: LINE_HEIGHT,
      color: TEXT_COLOR,
      backgroundColor: BG_COLOR,
      padding: `${PAD_Y} ${PAD_X}`,
      borderRadius: BORDER_RADIUS,
      zIndex: Z_INDEX,
      pointerEvents: 'none', // 不擋 canvas 上的點擊
      whiteSpace: 'pre', // 保留 setText 傳入的換行（用 \n 分行）
      userSelect: 'none', // 防止反白
    } as Partial<CSSStyleDeclaration>);
    document.body.appendChild(el);
    this.element = el;

    // Scene shutdown 時清掉 DOM，避免切場景殘留
    scene.events.once('shutdown', () => this.destroy());
    scene.events.once('destroy', () => this.destroy());
  }

  /** 整批更新顯示內容；每個元素為一行。 */
  setLines(lines: readonly string[]): void {
    this.element.textContent = lines.join('\n');
  }

  /** 顯隱切換（不 destroy）。 */
  setVisible(visible: boolean): void {
    this.element.style.display = visible ? 'block' : 'none';
  }

  /** 從 DOM 移除元素並解除 scene shutdown 監聽。 */
  destroy(): void {
    if (this.element.parentNode) {
      this.element.parentNode.removeChild(this.element);
    }
  }
}
